/* ============================================================
   BR BEAUTY & SUPPLIES — THEME JS
   v3.0 — Single Source of Truth, Clean Architecture
   ============================================================ */

(function () {
  'use strict';

  /* ============================================================
     UTILITIES
  ============================================================ */

  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));

  /**
   * Translation helper — reads from window.BRBeautyTranslations (set in theme.liquid).
   * Falls back to returning the key itself if no translation is found.
   * Supports simple placeholder replacement: t('key', { amount: '$10' })
   */
  function t(key, replacements) {
    let str = (window.BRBeautyTranslations && window.BRBeautyTranslations[key]) || key;
    if (replacements) {
      Object.keys(replacements).forEach(function (k) {
        str = str.replace('{{ ' + k + ' }}', replacements[k]);
      });
    }
    return str;
  }

  /**
   * Format cents (integer from Shopify API) to a display string.
   * Uses Shopify's money_format from theme, falls back to $ for USD stores.
   */
  function formatMoney(cents) {
    const value = (cents / 100).toFixed(2);
    // Check for Shopify money format (set in theme.liquid)
    const fmt = window.Shopify && window.Shopify.money_format;
    if (fmt) {
      return fmt
        .replace('{{amount}}', value)
        .replace('{{amount_no_decimals}}', Math.round(cents / 100))
        .replace('{{amount_with_comma_separator}}', value.replace('.', ','))
        .replace('{{amount_no_decimals_with_comma_separator}}', String(Math.round(cents / 100)));
    }
    return '$' + value;
  }

  /** Escape text for safe DOM insertion via textContent — used as reminder. */
  function escapeHtml(str) {
    const d = document.createElement('div');
    d.textContent = str;
    return d.innerHTML;
  }

  function debounce(fn, delay) {
    let timer;
    return function (...args) {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), delay);
    };
  }

  function throttle(fn, limit) {
    let last = 0;
    return function (...args) {
      const now = Date.now();
      if (now - last >= limit) { last = now; fn.apply(this, args); }
    };
  }

  /* ============================================================
     CART MODULE
     Single source of truth. All operations use Shopify AJAX API.
  ============================================================ */

  let _cartReservationInterval = null;

  // ---- API helpers ----

  async function _apiFetch(url, options = {}) {
    const res = await fetch(url, {
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      ...options,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.description || err.message || `Request failed: ${url}`);
    }
    return res.json();
  }

  function getCart() {
    return _apiFetch('/cart.js');
  }

  async function addToCart(variantId, qty = 1, properties = {}) {
    const data = await _apiFetch('/cart/add.js', {
      method: 'POST',
      body: JSON.stringify({ id: Number(variantId), quantity: qty, properties }),
    });
    return data;
  }

  async function removeFromCart(lineKey) {
    return _apiFetch('/cart/change.js', {
      method: 'POST',
      body: JSON.stringify({ id: lineKey, quantity: 0 }),
    });
  }

  async function updateQty(lineKey, qty) {
    return _apiFetch('/cart/change.js', {
      method: 'POST',
      body: JSON.stringify({ id: lineKey, quantity: qty }),
    });
  }

  // ---- UI helpers ----

  function updateCartCount(count) {
    $$('.cart-count-value').forEach(el => {
      el.textContent = count;
      const badge = el.closest('.cart-count');
      if (badge) badge.classList.toggle('hidden', count === 0);
    });
    if (count > 0) {
      const btn = $('.header-cart-btn');
      if (btn) {
        btn.style.animation = 'none';
        requestAnimationFrame(() => { btn.style.animation = 'cartPop 0.5s ease'; });
      }
    }
  }

  function openCart() {
    const overlay = $('#cart-overlay');
    if (overlay) overlay.classList.remove('hidden');
    document.body.style.overflow = 'hidden';
  }

  function closeCart() {
    const overlay = $('#cart-overlay');
    if (overlay) overlay.classList.add('hidden');
    document.body.style.overflow = '';
  }

  /**
   * Render cart data from Shopify /cart.js response into the drawer.
   * Never uses innerHTML with user-supplied strings — all text set via textContent.
   */
  function renderCart(cartData) {
    const container = $('#cart-items');
    if (!container) return;

    const items = cartData ? cartData.items : [];
    const totalCents = cartData ? cartData.total_price : 0;
    const itemCount = cartData ? cartData.item_count : 0;

    updateCartCount(itemCount);

    if (items.length === 0) {
      _renderEmptyCart(container);
      return;
    }

    $('#cart-footer') && ($('#cart-footer').classList.remove('hidden'));

    // Build item list using DOM — no innerHTML with user data
    container.textContent = '';
    items.forEach(item => {
      container.appendChild(_buildCartItem(item));
    });

    _renderCartTotals(cartData);
    _renderShippingProgress(totalCents);
    _renderCheckoutLink();
    _startReservationTimer();
  }

  function _renderEmptyCart(container) {
    container.textContent = '';

    const wrap = document.createElement('div');
    wrap.className = 'cart-empty';

    const icon = document.createElement('div');
    icon.className = 'cart-empty-icon';
    icon.textContent = '🛒';

    const msg = document.createElement('p');
    msg.textContent = t('cart_empty');

    const btn = document.createElement('button');
    btn.className = 'btn-primary';
    btn.style.marginTop = '16px';
    btn.textContent = t('cart_continue');
    btn.addEventListener('click', closeCart);

    wrap.appendChild(icon);
    wrap.appendChild(msg);
    wrap.appendChild(btn);
    container.appendChild(wrap);

    const footer = $('#cart-footer');
    if (footer) footer.classList.add('hidden');
  }

  function _buildCartItem(item) {
    const row = document.createElement('div');
    row.className = 'cart-item';
    row.dataset.itemKey = item.key;

    // Image
    const imgWrap = document.createElement('div');
    imgWrap.className = 'cart-item-image';
    const img = document.createElement('img');
    img.src = item.image || '';
    img.alt = '';
    img.loading = 'lazy';
    img.width = 80;
    img.height = 80;
    imgWrap.appendChild(img);

    // Info
    const info = document.createElement('div');
    info.className = 'cart-item-info';

    const name = document.createElement('div');
    name.className = 'cart-item-name';
    name.textContent = item.product_title;

    if (item.variant_title && item.variant_title !== 'Default Title') {
      const variant = document.createElement('span');
      variant.className = 'cart-item-variant';
      variant.textContent = ' — ' + item.variant_title;
      name.appendChild(variant);
    }

    const price = document.createElement('div');
    price.className = 'cart-item-price';
    price.textContent = formatMoney(item.price);

    info.appendChild(name);
    info.appendChild(price);

    // Qty controls
    const qtyWrap = document.createElement('div');
    qtyWrap.className = 'cart-qty';

    const btnMinus = document.createElement('button');
    btnMinus.className = 'cart-qty-btn';
    btnMinus.textContent = '−';
    btnMinus.setAttribute('aria-label', t('cart_decrease_qty'));
    btnMinus.addEventListener('click', () => _handleQtyChange(item.key, item.quantity - 1));

    const qtyVal = document.createElement('span');
    qtyVal.className = 'cart-qty-value';
    qtyVal.textContent = item.quantity;

    const btnPlus = document.createElement('button');
    btnPlus.className = 'cart-qty-btn';
    btnPlus.textContent = '+';
    btnPlus.setAttribute('aria-label', t('cart_increase_qty'));
    btnPlus.addEventListener('click', () => _handleQtyChange(item.key, item.quantity + 1));

    qtyWrap.appendChild(btnMinus);
    qtyWrap.appendChild(qtyVal);
    qtyWrap.appendChild(btnPlus);

    // Remove
    const removeBtn = document.createElement('button');
    removeBtn.className = 'cart-remove';
    removeBtn.setAttribute('aria-label', t('cart_remove_item'));
    removeBtn.textContent = '🗑️';
    removeBtn.addEventListener('click', () => _handleRemove(item.key));

    row.appendChild(imgWrap);
    row.appendChild(info);
    row.appendChild(qtyWrap);
    row.appendChild(removeBtn);

    return row;
  }

  const _debouncedQtyChange = debounce(async (lineKey, qty) => {
    try {
      const cartData = qty <= 0
        ? await removeFromCart(lineKey)
        : await updateQty(lineKey, qty);
      renderCart(cartData);
    } catch (e) {
      console.error('[BRBeauty] qty update failed:', e.message);
      _refreshAndRender();
    }
  }, 350);

  function _handleQtyChange(lineKey, newQty) {
    // Optimistic UI: update the display value immediately
    const row = $(`[data-item-key="${lineKey}"]`);
    if (row) {
      const val = row.querySelector('.cart-qty-value');
      if (val) val.textContent = Math.max(0, newQty);
    }
    _debouncedQtyChange(lineKey, newQty);
  }

  async function _handleRemove(lineKey) {
    try {
      const cartData = await removeFromCart(lineKey);
      renderCart(cartData);
    } catch (e) {
      console.error('[BRBeauty] remove failed:', e.message);
      _refreshAndRender();
    }
  }

  function _renderCartTotals(cartData) {
    const totalsEl = $('#cart-totals');
    if (!totalsEl) return;

    const subtotalCents = cartData.items_subtotal_price || cartData.total_price;
    const couponCode = localStorage.getItem('brb_coupon');

    totalsEl.textContent = '';

    const addRow = (labelText, valueText, extraClass) => {
      const row = document.createElement('div');
      row.className = 'cart-total-row' + (extraClass ? ' ' + extraClass : '');
      const lbl = document.createElement('span');
      lbl.textContent = labelText;
      const val = document.createElement('span');
      val.textContent = valueText;
      row.appendChild(lbl);
      row.appendChild(val);
      totalsEl.appendChild(row);
    };

    // Free shipping threshold from data attribute or default 5000 cents ($50)
    const thresholdEl = $('#shipping-progress') || $('#cart-shipping-progress');
    const thresholdCents = thresholdEl
      ? parseInt(thresholdEl.dataset.freeShippingThreshold || thresholdEl.dataset.threshold || '5000', 10)
      : 5000;

    addRow(t('cart_subtotal'), formatMoney(subtotalCents));

    if (couponCode) {
      // Visual-only: 5% discount shown. Actual discount applied at checkout via URL.
      const discountCents = Math.round(subtotalCents * 0.05);
      addRow(t('cart_coupon') + ' (' + couponCode + ')', '−' + formatMoney(discountCents), 'cart-total-row--discount');
    }

    const shippingCents = subtotalCents >= thresholdCents ? 0 : 1999;
    const shippingLabel = shippingCents === 0 ? t('cart_shipping_free') : formatMoney(shippingCents);
    addRow(t('cart_shipping'), shippingLabel, shippingCents === 0 ? 'cart-total-row--free' : '');

    // Final total row
    const finalRow = document.createElement('div');
    finalRow.className = 'cart-total-final';
    const finalLabel = document.createElement('span');
    finalLabel.className = 'cart-total-final-label';
    finalLabel.textContent = t('cart_total');
    const discountCentsTotal = couponCode ? Math.round(subtotalCents * 0.05) : 0;
    const totalCents = subtotalCents - discountCentsTotal + shippingCents;
    const finalValue = document.createElement('span');
    finalValue.className = 'cart-total-final-value';
    finalValue.textContent = formatMoney(totalCents);
    finalRow.appendChild(finalLabel);
    finalRow.appendChild(finalValue);
    totalsEl.appendChild(finalRow);

    // Installment note: 12x = totalCents / 12 cents each
    const installCents = Math.round(totalCents / 12);
    const installEl = $('#cart-installment');
    if (installEl) {
      installEl.textContent = '12x ' + t('product_installment_of') + ' ' + formatMoney(installCents) + ' ' + t('cart_installment_no_interest');
    }
  }

  function _renderShippingProgress(totalCents) {
    const progressEl = $('#shipping-progress') || $('#cart-shipping-progress');
    if (!progressEl) return;
    const thresholdCents = parseInt(progressEl.dataset.freeShippingThreshold || progressEl.dataset.threshold || '5000', 10);

    progressEl.textContent = '';

    const textDiv = document.createElement('div');
    textDiv.className = 'shipping-progress-text';

    const barWrap = document.createElement('div');
    barWrap.className = 'shipping-progress-bar';
    const fill = document.createElement('div');
    fill.className = 'shipping-progress-fill';

    if (totalCents >= thresholdCents) {
      textDiv.textContent = t('cart_free_shipping_congrats');
      fill.style.width = '100%';
    } else {
      const remaining = thresholdCents - totalCents;
      textDiv.textContent = t('cart_free_shipping_remaining', { amount: formatMoney(remaining) });
      const pct = Math.min(100, Math.round((totalCents / thresholdCents) * 100));
      fill.style.width = pct + '%';
    }

    barWrap.appendChild(fill);
    progressEl.appendChild(textDiv);
    progressEl.appendChild(barWrap);
  }

  /**
   * Update checkout link to include coupon code as a URL parameter.
   * Coupon system is visual-only in the cart; actual discount applied at /checkout?discount=CODE.
   */
  function _renderCheckoutLink() {
    const checkoutBtn = $('#checkout-link') || $('#cart-checkout-btn');
    if (!checkoutBtn) return;
    const couponCode = localStorage.getItem('brb_coupon');
    checkoutBtn.href = couponCode
      ? '/checkout?discount=' + encodeURIComponent(couponCode)
      : '/checkout';
  }

  async function _refreshAndRender() {
    try {
      const cartData = await getCart();
      renderCart(cartData);
    } catch (e) {
      console.error('[BRBeauty] could not refresh cart:', e.message);
    }
  }

  // ---- Coupon (visual feedback only) ----

  function applyCoupon(code) {
    const input = $('#coupon-input');
    const btn = $('#coupon-btn');
    const couponCode = (code || (input && input.value) || '').trim().toUpperCase();
    if (!couponCode) return;

    // These codes are visual-only; real validation happens at Shopify checkout
    const validCodes = ['BEAUTY5', 'BEAUTY10', 'PROMO15'];

    if (validCodes.includes(couponCode)) {
      localStorage.setItem('brb_coupon', couponCode);
      if (input) input.value = couponCode;
      if (btn) {
        btn.textContent = '✓ ' + t('coupon_applied');
        btn.style.cssText = 'background:#27AE60;color:#fff;border-color:#27AE60;';
      }
      _refreshAndRender(); // re-render totals and checkout link
    } else {
      if (input) {
        input.style.borderColor = '#E74C3C';
        setTimeout(() => { input.style.borderColor = ''; }, 2000);
      }
    }
  }

  // ---- Cart reservation timer ----

  function _startReservationTimer() {
    const el = $('#cart-timer') || $('#cart-reservation-timer');
    if (!el || _cartReservationInterval) return;

    const DURATION = 15 * 60;
    let remaining = parseInt(sessionStorage.getItem('brb_cart_timer') || String(DURATION), 10);

    el.classList.remove('hidden');
    const wrap = el.closest('.cart-reservation');
    if (wrap) wrap.classList.remove('hidden');

    function tick() {
      remaining = Math.max(0, remaining - 1);
      sessionStorage.setItem('brb_cart_timer', remaining);
      if (remaining === 0) {
        clearInterval(_cartReservationInterval);
        _cartReservationInterval = null;
        el.textContent = t('cart_reservation_expired');
        el.classList.add('timer-expired');
        return;
      }
      const m = String(Math.floor(remaining / 60)).padStart(2, '0');
      const s = String(remaining % 60).padStart(2, '0');
      el.textContent = t('cart_reserved') + ' ' + m + ':' + s;
    }

    tick();
    _cartReservationInterval = setInterval(tick, 1000);
  }

  // ---- Cart init ----

  function initCart() {
    // Open cart triggers
    $$('.open-cart').forEach(btn => btn.addEventListener('click', openCart));

    const closeBtn = $('.cart-close');
    if (closeBtn) closeBtn.addEventListener('click', closeCart);

    const backdrop = $('.cart-backdrop');
    if (backdrop) backdrop.addEventListener('click', closeCart);

    // Keyboard close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') closeCart();
    });

    // Coupon
    const couponBtn = $('#coupon-btn');
    if (couponBtn) couponBtn.addEventListener('click', () => applyCoupon());

    const couponInput = $('#coupon-input');
    if (couponInput) {
      couponInput.addEventListener('keypress', (e) => { if (e.key === 'Enter') applyCoupon(); });
      const stored = localStorage.getItem('brb_coupon');
      if (stored) {
        couponInput.value = stored;
        const btn = $('#coupon-btn');
        if (btn) btn.textContent = '✓ ' + t('coupon_applied');
      }
    }

    // Initial cart load
    _refreshAndRender();
  }

  // ---- Quick Add (delegated) ----

  document.addEventListener('click', async (e) => {
    const btn = e.target && e.target.closest ? e.target.closest('.quick-add-btn[data-variant-id]') : null;
    if (!btn) return;
    e.preventDefault();

    const variantId = btn.dataset.variantId;
    if (!variantId) return;

    const origHTML = btn.innerHTML;
    btn.disabled = true;
    btn.textContent = '…';

    try {
      await addToCart(variantId, 1);
      await _refreshAndRender();
      openCart();
      btn.textContent = '✓';
      btn.classList.add('quick-add-success');
      setTimeout(() => {
        btn.innerHTML = origHTML;
        btn.classList.remove('quick-add-success');
        btn.disabled = false;
      }, 1500);
    } catch (err) {
      console.error('[BRBeauty] quick add failed:', err.message);
      btn.textContent = '✗';
      btn.classList.add('quick-add-error');
      setTimeout(() => {
        btn.innerHTML = origHTML;
        btn.classList.remove('quick-add-error');
        btn.disabled = false;
      }, 1500);
    }
  });

  // ---- Product Card ATC (delegated) ----
  document.addEventListener('click', async (e) => {
    const btn = e.target && e.target.closest ? e.target.closest('.js-add-to-cart') : null;
    if (!btn || btn.closest('.quick-add-btn')) return;
    e.preventDefault();

    const variantId = btn.dataset.variantId;
    if (!variantId) return;

    const origHTML = btn.innerHTML;
    btn.disabled = true;
    btn.textContent = '\u2026';

    try {
      await addToCart(variantId, 1);
      await _refreshAndRender();
      openCart();
      btn.textContent = '\u2713';
      setTimeout(() => {
        btn.innerHTML = origHTML;
        btn.disabled = false;
      }, 1500);
    } catch (err) {
      console.error('[BRBeauty] card ATC failed:', err.message);
      btn.textContent = '\u2717';
      setTimeout(() => {
        btn.innerHTML = origHTML;
        btn.disabled = false;
      }, 1500);
    }
  });

  /* ============================================================
     HEADER MODULE
  ============================================================ */

  function initHeader() {
    // Scroll effect
    const header = $('.site-header');
    if (header) {
      window.addEventListener('scroll', throttle(() => {
        header.classList.toggle('scrolled', window.scrollY > 50);
      }, 100), { passive: true });
    }

    // Mobile menu — overlay-based slide-in panel
    const toggleBtn = $('.mobile-menu-toggle');
    const overlay = $('#mobile-menu-overlay');
    const menuBackdrop = $('#mobile-menu-backdrop');
    const menuCloseBtn = $('#mobile-menu-close');

    if (toggleBtn && overlay) {
      function openMenu() {
        overlay.classList.add('open');
        overlay.setAttribute('aria-hidden', 'false');
        document.body.style.overflow = 'hidden';
        toggleBtn.setAttribute('aria-expanded', 'true');
      }

      function closeMenu() {
        overlay.classList.remove('open');
        overlay.setAttribute('aria-hidden', 'true');
        document.body.style.overflow = '';
        toggleBtn.setAttribute('aria-expanded', 'false');
      }

      toggleBtn.addEventListener('click', () => {
        overlay.classList.contains('open') ? closeMenu() : openMenu();
      });
      if (menuBackdrop) menuBackdrop.addEventListener('click', closeMenu);
      if (menuCloseBtn) menuCloseBtn.addEventListener('click', closeMenu);
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeMenu(); });
    }

    // Language selector dropdown (header)
    const langSelector = $('#lang-selector');
    const langToggle = langSelector && langSelector.querySelector('.lang-selector__toggle');
    if (langSelector && langToggle) {
      langToggle.addEventListener('click', (e) => {
        e.stopPropagation();
        langSelector.classList.toggle('open');
        langToggle.setAttribute('aria-expanded', langSelector.classList.contains('open'));
      });
      document.addEventListener('click', (e) => {
        if (!langSelector.contains(e.target)) {
          langSelector.classList.remove('open');
          langToggle.setAttribute('aria-expanded', 'false');
        }
      });
    }
  }

  /* ============================================================
     PREDICTIVE SEARCH
  ============================================================ */

  function initPredictiveSearch() {
    const input = $('#search-input') || $('.search-input');
    if (!input) return;

    let dropdown = $('.search-dropdown');
    if (!dropdown) {
      dropdown = document.createElement('div');
      dropdown.className = 'search-dropdown';
      dropdown.setAttribute('role', 'listbox');
      input.parentElement.style.position = 'relative';
      input.parentElement.appendChild(dropdown);
    }

    let lastQuery = '';

    const doSearch = debounce(async (query) => {
      query = query.trim();
      if (query.length < 2) { dropdown.textContent = ''; dropdown.classList.remove('open'); return; }
      if (query === lastQuery) return;
      lastQuery = query;

      dropdown.textContent = '';
      const loading = document.createElement('div');
      loading.className = 'search-loading';
      loading.textContent = t('search_searching');
      dropdown.appendChild(loading);
      dropdown.classList.add('open');

      try {
        const url = '/search/suggest.json?q=' + encodeURIComponent(query) +
          '&resources[type]=product&resources[limit]=6';
        const data = await _apiFetch(url);
        const products = (data.resources && data.resources.results && data.resources.results.products) || [];

        dropdown.textContent = '';

        if (products.length === 0) {
          const msg = document.createElement('div');
          msg.className = 'search-no-results';
          msg.textContent = t('search_no_results');
          dropdown.appendChild(msg);
          return;
        }

        products.forEach(p => {
          const a = document.createElement('a');
          a.href = p.url;
          a.className = 'search-result-item';
          a.setAttribute('role', 'option');

          const imgEl = document.createElement('img');
          imgEl.src = (p.featured_image && p.featured_image.url) || '';
          imgEl.alt = '';
          imgEl.className = 'search-result-img';
          imgEl.loading = 'lazy';
          imgEl.width = 48;
          imgEl.height = 48;

          const infoDiv = document.createElement('div');
          infoDiv.className = 'search-result-info';

          const titleDiv = document.createElement('div');
          titleDiv.className = 'search-result-title';
          titleDiv.textContent = p.title;

          const priceDiv = document.createElement('div');
          priceDiv.className = 'search-result-price';
          priceDiv.textContent = p.price ? formatMoney(p.price) : '';

          infoDiv.appendChild(titleDiv);
          infoDiv.appendChild(priceDiv);
          a.appendChild(imgEl);
          a.appendChild(infoDiv);
          dropdown.appendChild(a);
        });
      } catch (err) {
        dropdown.textContent = '';
        dropdown.classList.remove('open');
      }
    }, 280);

    input.addEventListener('input', (e) => doSearch(e.target.value));
    input.addEventListener('focus', (e) => { if (e.target.value.length >= 2) doSearch(e.target.value); });
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') { dropdown.classList.remove('open'); input.blur(); }
    });
    document.addEventListener('click', (e) => {
      if (!input.contains(e.target) && !dropdown.contains(e.target)) {
        dropdown.classList.remove('open');
      }
    });
  }

  /* ============================================================
     HERO CAROUSEL
  ============================================================ */

  function initHeroCarousel() {
    const slides = $$('.hero-slide');
    const dots = $$('.hero-dot');
    if (slides.length === 0) return;

    let current = 0;
    let interval;
    const liveRegion = $('.hero-live-region');

    function goTo(index) {
      slides.forEach((s, i) => {
        s.classList.toggle('active', i === index);
        s.setAttribute('aria-hidden', i !== index ? 'true' : 'false');
      });
      dots.forEach((d, i) => {
        d.classList.toggle('active', i === index);
        d.setAttribute('aria-current', i === index ? 'true' : 'false');
      });
      current = index;
      if (liveRegion) liveRegion.textContent = t('slide_of', { current: index + 1, total: slides.length });
    }

    function next() { goTo((current + 1) % slides.length); }
    function prev() { goTo((current - 1 + slides.length) % slides.length); }

    function startAuto() { interval = setInterval(next, 5000); }
    function stopAuto() { clearInterval(interval); }

    goTo(0);
    startAuto();

    dots.forEach((dot, i) => {
      dot.addEventListener('click', () => { stopAuto(); goTo(i); startAuto(); });
    });

    const prevBtn = $('.hero-prev');
    const nextBtn = $('.hero-next');
    if (prevBtn) prevBtn.addEventListener('click', () => { stopAuto(); prev(); startAuto(); });
    if (nextBtn) nextBtn.addEventListener('click', () => { stopAuto(); next(); startAuto(); });

    // Pause on hover/focus
    const carousel = $('.hero-carousel');
    if (carousel) {
      carousel.addEventListener('mouseenter', stopAuto);
      carousel.addEventListener('mouseleave', startAuto);
      carousel.addEventListener('focusin', stopAuto);
      carousel.addEventListener('focusout', startAuto);
    }

    // Keyboard navigation
    document.addEventListener('keydown', (e) => {
      if (!carousel || !carousel.matches(':hover')) return;
      if (e.key === 'ArrowLeft') { stopAuto(); prev(); startAuto(); }
      if (e.key === 'ArrowRight') { stopAuto(); next(); startAuto(); }
    });

    // Touch swipe
    let touchStartX = 0;
    const hero = $('.hero-section') || carousel;
    if (hero) {
      hero.addEventListener('touchstart', (e) => { touchStartX = e.touches[0].clientX; }, { passive: true });
      hero.addEventListener('touchend', (e) => {
        const diff = touchStartX - e.changedTouches[0].clientX;
        if (Math.abs(diff) > 50) { stopAuto(); diff > 0 ? next() : prev(); startAuto(); }
      }, { passive: true });
    }
  }

  /* ============================================================
     COUNTDOWN TIMER
     Reads end time from data-end attribute on element.
     Falls back to sessionStorage for persistence.
  ============================================================ */

  function initCountdown() {
    // Find all countdown elements (supports dynamic IDs from Shopify sections)
    const countdowns = $$('.countdown[id^="flash-countdown"]');
    if (countdowns.length === 0) return;

    countdowns.forEach(el => {
      const storageKey = 'brb_countdown_end_' + el.id;
      const parentSection = el.closest('[data-countdown-duration]');
      const durationSec = parentSection ? parseInt(parentSection.dataset.countdownDuration || '0', 10) : 0;

      let endTime;
      const dataEnd = el.dataset.end;
      if (dataEnd) {
        endTime = new Date(dataEnd).getTime();
        if (isNaN(endTime)) endTime = null;
      }

      if (!endTime) {
        const stored = sessionStorage.getItem(storageKey);
        if (stored) {
          endTime = parseInt(stored, 10);
        } else if (durationSec > 0) {
          endTime = Date.now() + durationSec * 1000;
        } else {
          endTime = Date.now() + (4 * 3600 + 27 * 60 + 33) * 1000;
        }
      }
      sessionStorage.setItem(storageKey, endTime);

      function pad(n) { return String(n).padStart(2, '0'); }

      function tick() {
        const diff = Math.max(0, endTime - Date.now());
        el.textContent = '';

        if (diff === 0) {
          const msg = document.createElement('span');
          msg.className = 'countdown-ended';
          msg.textContent = t('countdown_ended');
          el.appendChild(msg);
          return;
        }

        const h = Math.floor(diff / 3600000);
        const m = Math.floor((diff % 3600000) / 60000);
        const s = Math.floor((diff % 60000) / 1000);

        const labels = {
          hours: t('countdown_hours'),
          min: t('countdown_min'),
          sec: t('countdown_sec')
        };

        [{ val: h, label: labels.hours }, { val: m, label: labels.min }, { val: s, label: labels.sec }].forEach(unit => {
          const block = document.createElement('div');
          block.className = 'countdown-block';
          const num = document.createElement('div');
          num.className = 'countdown-number';
          num.textContent = pad(unit.val);
          const lbl = document.createElement('div');
          lbl.className = 'countdown-label';
          lbl.textContent = unit.label;
          block.appendChild(num);
          block.appendChild(lbl);
          el.appendChild(block);
        });

        setTimeout(tick, 1000);
      }

      tick();
    });
  }

  /* ============================================================
     PRODUCT PAGE MODULE
  ============================================================ */

  function initProductPage() {
    _initPmGallery();
    _initPmVariants();
    _initPmQty();
    _initPmFormSubmit();
    _initPmBuyNow();
    _initPmShareButtons();
    _initPmStickyATC();
  }

  /* Gallery: thumbnail switching for product-main.liquid */
  function _initPmGallery() {
    const mainImg = $('#pm-main-image');
    const thumbs = $$('.js-pm-thumb');
    if (!mainImg || thumbs.length === 0) return;

    thumbs.forEach(thumb => {
      thumb.addEventListener('click', () => {
        const src = thumb.dataset.imgSrc;
        const alt = thumb.dataset.imgAlt || '';
        if (!src) return;

        mainImg.style.opacity = '0';
        mainImg.style.transition = 'opacity 0.25s ease';
        setTimeout(() => {
          mainImg.src = src;
          mainImg.alt = alt;
          mainImg.style.opacity = '1';
        }, 250);

        thumbs.forEach(th => {
          th.classList.remove('pm-gallery__thumb--active');
          th.setAttribute('aria-pressed', 'false');
        });
        thumb.classList.add('pm-gallery__thumb--active');
        thumb.setAttribute('aria-pressed', 'true');
      });
    });

    // Tap to zoom on mobile
    let zoomed = false;
    mainImg.addEventListener('click', () => {
      if (window.innerWidth > 768) return;
      zoomed = !zoomed;
      mainImg.style.transform = zoomed ? 'scale(2)' : '';
      mainImg.style.transition = 'transform 0.3s ease';
      mainImg.style.cursor = zoomed ? 'zoom-out' : 'zoom-in';
    });
  }

  /* Variant selector: button-based options for product-main.liquid */
  function _initPmVariants() {
    const variantsJsonEl = $('#pm-variants-json');
    if (!variantsJsonEl) return;

    let variants;
    try { variants = JSON.parse(variantsJsonEl.textContent); } catch (e) { return; }

    const variantIdInput = $('#pm-variant-id');
    const optionBtns = $$('.pm-variants__btn');

    // Track selected options
    const selectedOptions = [];
    $$('.pm-variants__group').forEach((group, i) => {
      const activeBtn = group.querySelector('.pm-variants__btn.active');
      selectedOptions[i] = activeBtn ? activeBtn.dataset.optionValue : '';
    });

    optionBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        const index = parseInt(btn.dataset.optionIndex, 10);
        const value = btn.dataset.optionValue;
        selectedOptions[index] = value;

        // Update button states within this group
        const group = btn.closest('.pm-variants__group');
        $$('.pm-variants__btn', group).forEach(b => {
          b.classList.toggle('active', b === btn);
          b.setAttribute('aria-pressed', b === btn ? 'true' : 'false');
        });

        // Update selected value display
        const valDisplay = group.querySelector('.pm-variants__selected');
        if (valDisplay) valDisplay.textContent = value;

        // Find matching variant
        const match = variants.find(v => {
          return v.options.every((opt, i) => opt === selectedOptions[i]);
        });

        if (match) {
          if (variantIdInput) variantIdInput.value = match.id;

          // Update price
          const priceEl = $('.pm-price-block__current');
          if (priceEl) priceEl.textContent = formatMoney(match.price);

          const compareEl = $('.pm-price-block__original');
          if (compareEl) {
            if (match.compare_at_price && match.compare_at_price > match.price) {
              compareEl.textContent = formatMoney(match.compare_at_price);
              compareEl.style.display = '';
            } else {
              compareEl.style.display = 'none';
            }
          }

          // Update savings badge
          const savingsEl = $('.pm-price-block__savings');
          if (savingsEl) {
            if (match.compare_at_price && match.compare_at_price > match.price) {
              const savings = match.compare_at_price - match.price;
              savingsEl.textContent = t('product_save') + ' ' + formatMoney(savings);
              savingsEl.style.display = '';
            } else {
              savingsEl.style.display = 'none';
            }
          }

          // Update installment
          const installEl = $('.pm-price-block__installment');
          if (installEl) {
            const installCents = Math.round(match.price / 12);
            installEl.innerHTML = '<strong>12x</strong> ' + t('product_installment_of') + ' <strong>' + formatMoney(installCents) + '</strong> ' + t('cart_installment_no_interest');
          }

          // Update ATC button
          const atcBtn = $('#pm-atc-btn');
          if (atcBtn) {
            atcBtn.disabled = !match.available;
            if (match.available) {
              atcBtn.innerHTML = '<span aria-hidden="true">🛒</span> ' + t('product_add_to_cart');
            } else {
              atcBtn.textContent = t('product_sold_out');
            }
          }

          // Update buy now link
          const buyNowBtn = $('#pm-buy-now-btn');
          if (buyNowBtn) {
            buyNowBtn.href = '/cart/' + match.id + ':1';
            buyNowBtn.dataset.variantId = match.id;
          }

          // Update featured image if variant has one
          if (match.featured_image) {
            const mainImg = $('#pm-main-image');
            if (mainImg) {
              mainImg.style.opacity = '0';
              setTimeout(() => {
                mainImg.src = match.featured_image.src;
                mainImg.style.opacity = '1';
              }, 250);
            }
          }

          // Update URL
          if (history.replaceState) {
            const url = new URL(window.location.href);
            url.searchParams.set('variant', match.id);
            history.replaceState(null, '', url.toString());
          }
        }
      });
    });
  }

  /* Quantity +/- buttons for product-main.liquid */
  function _initPmQty() {
    document.addEventListener('click', (e) => {
      const btn = e.target && e.target.closest ? e.target.closest('.js-pm-qty-dec, .js-pm-qty-inc, .pm-qty__btn') : null;
      if (!btn) return;

      const targetId = btn.dataset.qtyTarget;
      const input = targetId ? $('#' + targetId) : btn.closest('.pm-qty, .cart-qty')?.querySelector('input');
      if (!input) return;

      const delta = parseInt(btn.dataset.qtyDelta || (btn.classList.contains('js-pm-qty-inc') ? '1' : '-1'), 10);
      const min = parseInt(input.min || '1', 10);
      const max = parseInt(input.max || '99', 10);
      let val = parseInt(input.value || '1', 10) + delta;
      input.value = Math.min(max, Math.max(min, val));

      btn.style.transform = 'scale(0.88)';
      setTimeout(() => { btn.style.transform = ''; }, 150);
    });
  }

  /* AJAX form submission for product-main.liquid */
  function _initPmFormSubmit() {
    const form = $('#pm-product-form');
    if (!form) return;

    form.addEventListener('submit', async (e) => {
      e.preventDefault();

      const variantId = form.querySelector('input[name="id"]')?.value;
      const qty = parseInt(form.querySelector('input[name="quantity"]')?.value || '1', 10);
      if (!variantId) return;

      const atcBtn = $('#pm-atc-btn');
      const origHTML = atcBtn ? atcBtn.innerHTML : '';
      if (atcBtn) { atcBtn.disabled = true; atcBtn.textContent = t('product_adding'); }

      try {
        await addToCart(variantId, qty);
        await _refreshAndRender();

        // Show notification
        const notif = $('#pm-atc-notification');
        if (notif) {
          notif.classList.remove('pm-atc-notification--hidden');
          setTimeout(() => notif.classList.add('pm-atc-notification--hidden'), 4000);
        }

        openCart();
      } catch (err) {
        console.error('[BRBeauty] ATC failed:', err.message);
        if (atcBtn) atcBtn.textContent = t('product_error');
      } finally {
        setTimeout(() => {
          if (atcBtn) { atcBtn.disabled = false; atcBtn.innerHTML = origHTML; }
        }, 1500);
      }
    });

    // Also handle .js-open-cart clicks (notification "Ver Carrinho" btn)
    document.addEventListener('click', (e) => {
      if (e.target && e.target.closest && e.target.closest('.js-open-cart')) openCart();
    });
  }

  /* Buy Now: redirect to checkout */
  function _initPmBuyNow() {
    document.addEventListener('click', async (e) => {
      const btn = e.target && e.target.closest ? e.target.closest('.js-buy-now') : null;
      if (!btn) return;
      e.preventDefault();
      const form = btn.closest('form');
      const variantIdInput = form ? form.querySelector('input[name="id"]') : null;
      const variantId = variantIdInput ? variantIdInput.value : btn.dataset.variantId;
      if (!variantId) return;
      const qtyInput = form ? form.querySelector('input[name="quantity"]') : null;
      const qty = qtyInput ? qtyInput.value : 1;

      btn.style.opacity = '0.6';
      btn.style.pointerEvents = 'none';

      try {
        await addToCart(variantId, qty);
        window.location.href = '/checkout';
      } catch (err) {
        console.error('[BRBeauty] Buy now failed:', err.message);
        btn.style.opacity = '';
        btn.style.pointerEvents = '';
      }
    });
  }

  /* Share buttons: copy link */
  function _initPmShareButtons() {
    document.addEventListener('click', (e) => {
      const btn = e.target && e.target.closest ? e.target.closest('.js-pm-copy-link') : null;
      if (!btn) return;
      const url = btn.dataset.copyUrl || window.location.href;
      navigator.clipboard.writeText(url).then(() => {
        const orig = btn.textContent;
        btn.textContent = '✓ ' + t('product_copied');
        setTimeout(() => { btn.textContent = orig; }, 2000);
      }).catch(() => {});
    });
  }

  /* Sticky ATC bar: show when main ATC button scrolls out of view */
  function _initPmStickyATC() {
    const atcBtn = $('#pm-atc-btn');
    const stickyBar = $('[data-sticky-atc]');
    if (!atcBtn || !stickyBar) return;

    const observer = new IntersectionObserver((entries) => {
      const visible = !entries[0].isIntersecting;
      stickyBar.classList.toggle('sticky-atc--visible', visible);
      stickyBar.setAttribute('aria-hidden', !visible);
      document.body.classList.toggle('body--sticky-atc-visible', visible);
    }, { threshold: 0 });

    observer.observe(atcBtn);

    const stickyBtn = stickyBar.querySelector('[data-sticky-atc-btn]');
    if (stickyBtn) {
      stickyBtn.addEventListener('click', async () => {
        const variantId = $('#pm-variant-id')?.value;
        const qty = parseInt($('#pm-qty-input')?.value || '1', 10);
        if (!variantId) return;
        stickyBtn.disabled = true;
        stickyBtn.textContent = t('product_adding');
        try {
          await addToCart(variantId, qty);
          await _refreshAndRender();
          openCart();
        } catch (err) {
          console.error('[BRBeauty] sticky ATC failed:', err.message);
        } finally {
          stickyBtn.disabled = false;
          stickyBtn.textContent = t('product_add_to_cart');
        }
      });
    }
  }

  /* ============================================================
     UPSELL MODULE — "Frequently Bought Together"
  ============================================================ */

  function initUpsell() {
    const upsellForm = $('#upsell-form');
    if (!upsellForm) return;

    function recalcUpsell() {
      let total = 0;
      $$('input[type="checkbox"]:checked', upsellForm).forEach(cb => {
        total += parseInt(cb.dataset.priceCents || '0', 10);
      });
      const totalEl = upsellForm.querySelector('.upsell-total');
      if (totalEl) totalEl.textContent = formatMoney(total);
    }

    $$('input[type="checkbox"]', upsellForm).forEach(cb => {
      cb.addEventListener('change', recalcUpsell);
    });

    recalcUpsell();

    upsellForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      const checked = $$('input[type="checkbox"]:checked', upsellForm);
      if (checked.length === 0) return;

      const submitBtn = upsellForm.querySelector('[type="submit"]');
      if (submitBtn) { submitBtn.disabled = true; submitBtn.textContent = t('product_adding'); }

      try {
        for (const cb of checked) {
          if (cb.dataset.variantId) await addToCart(cb.dataset.variantId, 1);
        }
        await _refreshAndRender();
        openCart();
      } catch (err) {
        console.error('[BRBeauty] upsell add failed:', err.message);
      } finally {
        if (submitBtn) { submitBtn.disabled = false; submitBtn.textContent = t('product_add_to_cart'); }
      }
    });
  }

  /* ============================================================
     POPUPS MODULE
  ============================================================ */

  function _focusTrap(el) {
    const focusable = $$('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])', el);
    if (focusable.length === 0) return () => {};
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    function handler(e) {
      if (e.key !== 'Tab') return;
      if (e.shiftKey) { if (document.activeElement === first) { e.preventDefault(); last.focus(); } }
      else { if (document.activeElement === last) { e.preventDefault(); first.focus(); } }
    }
    el.addEventListener('keydown', handler);
    first.focus();
    return () => el.removeEventListener('keydown', handler);
  }

  function _initPopup(popupEl, sessionKey) {
    if (!popupEl) return;

    let removeTrap = () => {};

    function closePopup() {
      popupEl.classList.add('hidden');
      document.body.classList.remove('popup-open');
      removeTrap();
    }

    function openPopup() {
      popupEl.classList.remove('hidden');
      document.body.classList.add('popup-open');
      removeTrap = _focusTrap(popupEl);
    }

    popupEl.querySelectorAll('.popup-close, .popup-close-btn').forEach(btn => {
      btn.addEventListener('click', closePopup);
    });
    popupEl.addEventListener('click', (e) => { if (e.target === popupEl) closePopup(); });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closePopup(); });

    return { open: openPopup, close: closePopup };
  }

  function initPopups() {
    // Welcome coupon popup
    const welcome = $('#popup-welcome');
    if (welcome && !sessionStorage.getItem('brb_welcome_shown')) {
      const ctrl = _initPopup(welcome, 'brb_welcome_shown');
      setTimeout(() => {
        ctrl.open();
        sessionStorage.setItem('brb_welcome_shown', '1');
      }, 2000);

      const couponBox = welcome.querySelector('.coupon-box');
      if (couponBox) {
        couponBox.addEventListener('click', () => {
          const code = couponBox.dataset.code || 'BEAUTY5';
          navigator.clipboard.writeText(code).catch(() => {});
          localStorage.setItem('brb_coupon', code);
          const lbl = couponBox.querySelector('.coupon-copy-label');
          if (lbl) lbl.textContent = '✓ ' + t('popup_copied');
          setTimeout(() => { if (lbl) lbl.textContent = t('popup_copy'); }, 2000);
        });
      }

      const startBtn = welcome.querySelector('.popup-start-btn');
      if (startBtn && ctrl) startBtn.addEventListener('click', ctrl.close);
    }

    // Newsletter popup (wait for welcome to close, then 15s)
    const newsletter = $('#popup-newsletter');
    if (newsletter && !sessionStorage.getItem('brb_newsletter_shown')) {
      setTimeout(() => {
        const welcomeOpen = welcome && !welcome.classList.contains('hidden');
        if (welcomeOpen) {
          // Wait for welcome to close then delay another 5s
          const waitForClose = setInterval(() => {
            if (!welcome || welcome.classList.contains('hidden')) {
              clearInterval(waitForClose);
              setTimeout(() => showNewsletter(), 5000);
            }
          }, 500);
        } else {
          showNewsletter();
        }
      }, 15000);

      function showNewsletter() {
        if (sessionStorage.getItem('brb_newsletter_shown')) return;
        const ctrl = _initPopup(newsletter);
        ctrl.open();
        sessionStorage.setItem('brb_newsletter_shown', '1');

        const form = newsletter.querySelector('.newsletter-popup-form');
        if (form) {
          form.addEventListener('submit', (e) => {
            e.preventDefault();
            const body = newsletter.querySelector('.popup-body');
            if (body) {
              body.textContent = '';
              const wrap = document.createElement('div');
              wrap.style.cssText = 'text-align:center;padding:20px;';
              const icon = document.createElement('div');
              icon.style.fontSize = '48px';
              icon.textContent = '✅';
              const h3 = document.createElement('h3');
              h3.style.cssText = 'color:#2C1810;margin-top:8px;';
              h3.textContent = t('popup_signup_success');
              const p = document.createElement('p');
              p.style.cssText = 'color:#888;font-size:13px;margin-top:6px;';
              p.textContent = t('popup_check_email');
              const closeBtn2 = document.createElement('button');
              closeBtn2.className = 'btn-primary';
              closeBtn2.style.marginTop = '16px';
              closeBtn2.textContent = t('general_close');
              closeBtn2.addEventListener('click', ctrl.close);
              wrap.appendChild(icon);
              wrap.appendChild(h3);
              wrap.appendChild(p);
              wrap.appendChild(closeBtn2);
              body.appendChild(wrap);
            }
          });
        }
      }
    }

    // Exit intent popup
    const exitPopup = $('#exit-intent-popup');
    if (exitPopup && !sessionStorage.getItem('brb_exit_shown')) {
      let triggered = false;
      document.addEventListener('mouseleave', (e) => {
        if (e.clientY > 10 || triggered) return;
        triggered = true;
        sessionStorage.setItem('brb_exit_shown', '1');
        const ctrl = _initPopup(exitPopup);
        ctrl.open();
        const stayBtn = exitPopup.querySelector('.exit-stay-btn');
        if (stayBtn) stayBtn.addEventListener('click', ctrl.close);
      });
    }
  }

  /* ============================================================
     SOCIAL PROOF MODULE
  ============================================================ */

  function initSocialProof() {
    const container = $('#social-proof-container');
    if (!container) return;
    if (sessionStorage.getItem('brb_social_dismissed')) return;

    // Read notifications from data attributes on the section element
    const section = container.closest('[data-social-proof]') || container;
    let notifications;
    try {
      notifications = JSON.parse(section.dataset.notifications || 'null');
    } catch (e) { notifications = null; }

    if (!notifications || notifications.length === 0) {
      notifications = [
        { name: 'Ana S.', city: 'São Paulo', product: 'Máscara Capilar Gold', time: '2 min atrás' },
        { name: 'Carla M.', city: 'Rio de Janeiro', product: 'Kit Hidratação Intensa', time: '5 min atrás' },
        { name: 'Fernanda L.', city: 'Curitiba', product: 'Óleo Argan Premium', time: '8 min atrás' },
        { name: 'Juliana R.', city: 'Belo Horizonte', product: 'Shampoo Profissional', time: '12 min atrás' },
      ];
    }

    let index = 0;
    let hideTimer, showTimer;

    function showNext() {
      if (sessionStorage.getItem('brb_social_dismissed')) return;
      const data = notifications[index];
      index = (index + 1) % notifications.length;

      container.textContent = '';
      const toast = document.createElement('div');
      toast.className = 'social-proof-toast';
      toast.setAttribute('role', 'alert');
      toast.setAttribute('aria-live', 'polite');

      const avatar = document.createElement('div');
      avatar.className = 'social-proof-avatar';
      avatar.textContent = data.name.charAt(0);

      const textDiv = document.createElement('div');
      textDiv.className = 'social-proof-text';

      const strong = document.createElement('strong');
      strong.textContent = data.name;
      const fromSpan = document.createTextNode(' ' + t('social_proof_from') + ' ' + data.city);
      const boughtDiv = document.createElement('div');
      boughtDiv.textContent = t('social_proof_bought') + ' ' + data.product;
      const timeDiv = document.createElement('div');
      timeDiv.className = 'social-proof-time';
      timeDiv.textContent = data.time;

      textDiv.appendChild(strong);
      textDiv.appendChild(fromSpan);
      textDiv.appendChild(boughtDiv);
      textDiv.appendChild(timeDiv);

      const closeBtn = document.createElement('button');
      closeBtn.className = 'social-proof-close';
      closeBtn.setAttribute('aria-label', t('general_close'));
      closeBtn.textContent = '×';
      closeBtn.addEventListener('click', () => {
        container.classList.remove('show');
        sessionStorage.setItem('brb_social_dismissed', '1');
        clearTimeout(hideTimer);
        clearTimeout(showTimer);
      });

      toast.appendChild(avatar);
      toast.appendChild(textDiv);
      toast.appendChild(closeBtn);
      container.appendChild(toast);
      container.classList.add('show');

      hideTimer = setTimeout(() => {
        container.classList.remove('show');
        const delay = 15000 + Math.random() * 5000;
        showTimer = setTimeout(showNext, delay);
      }, 5000);
    }

    setTimeout(showNext, 4000);
  }

  /* ============================================================
     WISHLIST MODULE
     Key: 'brb_wishlist' (array of product IDs as strings)
  ============================================================ */

  const Wishlist = (function () {
    const KEY = 'brb_wishlist';

    function get() {
      try { return JSON.parse(localStorage.getItem(KEY) || '[]'); } catch (e) { return []; }
    }

    function toggle(productId) {
      const list = get();
      const id = String(productId);
      const idx = list.indexOf(id);
      idx === -1 ? list.push(id) : list.splice(idx, 1);
      localStorage.setItem(KEY, JSON.stringify(list));
      _sync();
      _updateCount();
      return idx === -1; // true = added
    }

    function has(productId) { return get().includes(String(productId)); }

    function _sync() {
      $$('[data-wishlist-id]').forEach(btn => {
        const id = btn.dataset.wishlistId;
        const active = has(id);
        btn.classList.toggle('wishlisted', active);
        btn.setAttribute('aria-pressed', String(active));
        btn.title = active ? t('wishlist_remove') : t('wishlist_add');
      });
    }

    function _updateCount() {
      const count = get().length;
      $$('.wishlist-count').forEach(el => {
        el.textContent = count;
        el.classList.toggle('hidden', count === 0);
      });
    }

    function init() {
      document.addEventListener('click', (e) => {
        const btn = e.target && e.target.closest ? e.target.closest('[data-wishlist-id]') : null;
        if (!btn) return;
        e.preventDefault();
        toggle(btn.dataset.wishlistId);
        btn.style.transform = 'scale(1.35)';
        setTimeout(() => { btn.style.transform = ''; }, 300);
      });
      _sync();
      _updateCount();
    }

    return { get, toggle, has, init };
  })();

  /* ============================================================
     RECENTLY VIEWED MODULE
     Stores product handles in localStorage: 'brb_recently_viewed'
     Fetches product data via /products/{handle}.json on render.
  ============================================================ */

  const RecentlyViewed = (function () {
    const KEY = 'brb_recently_viewed';
    const MAX = 8;

    function getHandles() {
      try { return JSON.parse(localStorage.getItem(KEY) || '[]'); } catch (e) { return []; }
    }

    function track(handle) {
      if (!handle) return;
      const list = getHandles().filter(h => h !== handle);
      list.unshift(handle);
      localStorage.setItem(KEY, JSON.stringify(list.slice(0, MAX)));
    }

    async function render(currentHandle) {
      const container = $('#recently-viewed-container');
      if (!container) return;

      let handles = getHandles().filter(h => h !== currentHandle).slice(0, 6);
      if (handles.length === 0) {
        const section = container.closest('.recently-viewed-section');
        if (section) section.classList.add('hidden');
        return;
      }

      // Fetch product data via AJAX API
      const products = [];
      for (const handle of handles) {
        try {
          const data = await _apiFetch('/products/' + handle + '.json');
          if (data && data.product) products.push(data.product);
        } catch (e) { /* skip missing products */ }
      }

      if (products.length === 0) {
        const section = container.closest('.recently-viewed-section');
        if (section) section.classList.add('hidden');
        return;
      }

      container.textContent = '';
      products.forEach(p => {
        const variant = p.variants && p.variants[0];
        const priceCents = variant ? parseInt(variant.price * 100, 10) || 0 : 0;
        const imgSrc = (p.images && p.images[0] && p.images[0].src) || '';

        const a = document.createElement('a');
        a.href = '/products/' + p.handle;
        a.className = 'product-card recently-viewed-card scroll-reveal';

        const imgWrap = document.createElement('div');
        imgWrap.className = 'product-card-image';
        const img = document.createElement('img');
        img.src = imgSrc;
        img.alt = '';
        img.loading = 'lazy';
        imgWrap.appendChild(img);

        const info = document.createElement('div');
        info.className = 'product-card-info';

        const title = document.createElement('div');
        title.className = 'product-card-title';
        title.textContent = p.title;

        const price = document.createElement('div');
        price.className = 'product-card-price';
        price.textContent = priceCents ? formatMoney(priceCents) : '';

        info.appendChild(title);
        info.appendChild(price);
        a.appendChild(imgWrap);
        a.appendChild(info);
        container.appendChild(a);
      });
    }

    return { getHandles, track, render };
  })();

  /* ============================================================
     SCROLL REVEAL
  ============================================================ */

  function initScrollReveal() {
    if (!('IntersectionObserver' in window)) {
      $$('.scroll-reveal').forEach(el => el.classList.add('revealed'));
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        const el = entry.target;
        const grid = el.closest('.scroll-reveal-grid');
        if (grid) {
          const siblings = $$('.scroll-reveal', grid);
          const idx = siblings.indexOf(el);
          el.style.transitionDelay = (idx * 80) + 'ms';
        }
        el.classList.add('revealed');
        observer.unobserve(el);
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });

    $$('.scroll-reveal').forEach(el => observer.observe(el));

    // Expose observer so dynamically added elements can be observed
    window._brbRevealObserver = observer;
  }

  /* ============================================================
     BACK TO TOP
  ============================================================ */

  function initBackToTop() {
    const btn = $('#back-to-top');
    if (!btn) return;
    window.addEventListener('scroll', throttle(() => {
      btn.classList.toggle('visible', window.scrollY > 300);
    }, 200), { passive: true });
    btn.addEventListener('click', () => window.scrollTo({ top: 0, behavior: 'smooth' }));
  }

  /* ============================================================
     ANNOUNCEMENT BAR
  ============================================================ */

  function initAnnouncementBar() {
    const bar = $('.announcement-bar');
    const closeBtn = bar && bar.querySelector('.js-announcement-dismiss, .announcement-close');
    if (!bar || !closeBtn) return;

    if (sessionStorage.getItem('brb_announcement_dismissed')) {
      bar.style.display = 'none';
      return;
    }

    closeBtn.addEventListener('click', () => {
      bar.style.maxHeight = bar.offsetHeight + 'px';
      bar.style.overflow = 'hidden';
      bar.style.transition = 'max-height 0.3s ease, opacity 0.3s ease, padding 0.3s ease';
      requestAnimationFrame(() => {
        bar.style.maxHeight = '0';
        bar.style.opacity = '0';
        bar.style.padding = '0';
      });
      setTimeout(() => { bar.style.display = 'none'; }, 350);
      sessionStorage.setItem('brb_announcement_dismissed', '1');
    });
  }

  /* ============================================================
     PRODUCT TABS
  ============================================================ */

  function initProductTabs() {
    const tabs = $$('.tab-btn');
    const grids = $$('.products-tab-content');
    if (tabs.length === 0) return;

    tabs.forEach(tab => {
      tab.addEventListener('click', () => {
        const target = tab.dataset.tab;
        tabs.forEach(tb => tb.classList.toggle('active', tb === tab));
        grids.forEach(g => {
          const show = g.dataset.tab === target;
          g.style.display = show ? 'grid' : 'none';
          if (show) {
            g.querySelectorAll('.product-card').forEach((card, i) => {
              card.style.animation = 'slideUp 0.4s ease ' + (i * 0.06) + 's both';
            });
          }
        });
      });
    });
  }

  /* ============================================================
     LAZY IMAGE LOADING
  ============================================================ */

  function initLazyImages() {
    if (!('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        const img = entry.target;
        const src = img.dataset.src;
        if (!src) return;
        const temp = new Image();
        temp.onload = () => {
          img.src = src;
          img.classList.add('lazy-loaded');
          img.removeAttribute('data-src');
        };
        temp.src = src;
        observer.unobserve(img);
      });
    }, { rootMargin: '200px 0px' });

    $$('img[data-src]').forEach(img => {
      img.classList.add('lazy-blur');
      observer.observe(img);
    });
  }

  /* ============================================================
     GLOBAL API
     All functions that Liquid sections call via onclick must
     be assigned directly to window as well as window.BRBeauty.
  ============================================================ */

  window.BRBeauty = {
    addToCart,
    removeFromCart,
    updateQty,
    openCart,
    closeCart,
    getCart,
    formatMoney,
    applyCoupon,
    wishlist: Wishlist,
    recentlyViewed: RecentlyViewed,
  };

  // Direct globals for onclick="..." handlers in Liquid sections
  window.openCart = openCart;
  window.closeCart = closeCart;
  window.addToCart = addToCart;
  window.applyCoupon = applyCoupon;

  /* ============================================================
     INIT — DOMContentLoaded
  ============================================================ */

  document.addEventListener('DOMContentLoaded', async () => {
    // Core layout
    initHeader();
    initAnnouncementBar();
    initBackToTop();
    initScrollReveal();
    initLazyImages();

    // Navigation & search
    initPredictiveSearch();

    // Sections
    initHeroCarousel();
    initCountdown();
    initProductTabs();

    // Cart (loads from Shopify API)
    initCart();

    // Product page
    initProductPage();
    initUpsell();

    // Engagement
    initPopups();
    initSocialProof();

    // Wishlist
    Wishlist.init();

    // Recently viewed
    const handleEl = document.querySelector('[data-product-handle]');
    if (handleEl && handleEl.dataset.productHandle) {
      RecentlyViewed.track(handleEl.dataset.productHandle);
    }
    const currentHandle = handleEl ? handleEl.dataset.productHandle : null;
    await RecentlyViewed.render(currentHandle);

    // Observe any newly rendered .scroll-reveal elements
    if (window._brbRevealObserver) {
      $$('.scroll-reveal:not(.revealed)').forEach(el => window._brbRevealObserver.observe(el));
    }

    // AI-powered features
    initSmartRecommendations();
    initPerformanceMonitor();
    initSmartCouponHints();
    initViewportOptimizer();
  });

  /* ============================================================
     AI-POWERED SMART RECOMMENDATIONS
     Tracks user behavior to show personalized product suggestions
  ============================================================ */
  function initSmartRecommendations() {
    var behaviorKey = 'brb_user_behavior';
    var stored = sessionStorage.getItem(behaviorKey);
    var behavior = stored ? JSON.parse(stored) : { views: [], clicks: [], categories: {}, lastVisit: 0 };

    // Track product views
    var productSection = $('[data-product-handle]');
    if (productSection) {
      var handle = productSection.dataset.productHandle;
      var id = productSection.dataset.productId;
      if (handle && behavior.views.indexOf(handle) === -1) {
        behavior.views.push(handle);
        if (behavior.views.length > 20) behavior.views.shift();
      }
      // Track category affinity from product tags
      $$('[data-product-tags] span, .product-tag').forEach(function(tag) {
        var cat = tag.textContent.trim().toLowerCase();
        if (cat) behavior.categories[cat] = (behavior.categories[cat] || 0) + 1;
      });
    }

    // Track add-to-cart clicks for affinity
    document.addEventListener('click', function(e) {
      var btn = e.target && e.target.closest ? e.target.closest('.js-add-to-cart') : null;
      if (btn) {
        var pid = btn.dataset.productId;
        if (pid && behavior.clicks.indexOf(pid) === -1) {
          behavior.clicks.push(pid);
          if (behavior.clicks.length > 10) behavior.clicks.shift();
        }
        sessionStorage.setItem(behaviorKey, JSON.stringify(behavior));
      }
    });

    behavior.lastVisit = Date.now();
    sessionStorage.setItem(behaviorKey, JSON.stringify(behavior));

    // Smart empty cart recommendations based on behavior
    var emptyGrid = $('#cart-empty-grid');
    if (emptyGrid && behavior.views.length > 0) {
      var recentHandles = behavior.views.slice(-4).reverse();
      recentHandles.forEach(function(h) {
        fetch('/products/' + h + '.json')
          .then(function(r) { return r.ok ? r.json() : null; })
          .then(function(data) {
            if (!data || !data.product) return;
            var p = data.product;
            var img = (p.images && p.images[0]) ? p.images[0].src : '';
            var price = p.variants[0] ? formatMoney(p.variants[0].price) : '';
            var card = document.createElement('a');
            card.href = '/products/' + p.handle;
            card.className = 'cart-empty-featured__item';
            card.style.cssText = 'display:block;text-align:center;text-decoration:none;';
            if (img) {
              var imgEl = document.createElement('img');
              imgEl.src = img.replace(/\.([^.]+)$/, '_200x.$1');
              imgEl.alt = p.title;
              imgEl.style.cssText = 'width:100%;border-radius:8px;margin-bottom:6px;';
              imgEl.loading = 'lazy';
              card.appendChild(imgEl);
            }
            var title = document.createElement('div');
            title.style.cssText = 'font-size:11px;font-weight:600;color:var(--accent);line-height:1.3;margin-bottom:2px;';
            title.textContent = p.title.length > 30 ? p.title.substring(0, 30) + '…' : p.title;
            card.appendChild(title);
            var priceEl = document.createElement('div');
            priceEl.style.cssText = 'font-size:12px;font-weight:700;color:var(--primary);';
            priceEl.textContent = price;
            card.appendChild(priceEl);
            emptyGrid.appendChild(card);
          })
          .catch(function() {});
      });
    }
  }

  /* ============================================================
     PERFORMANCE MONITOR
     Tracks Core Web Vitals and optimizes rendering
  ============================================================ */
  function initPerformanceMonitor() {
    // Defer non-critical images
    if ('requestIdleCallback' in window) {
      requestIdleCallback(function() {
        $$('img[loading="lazy"]:not([src])').forEach(function(img) {
          if (img.dataset.src) {
            img.src = img.dataset.src;
          }
        });
      });
    }

    // Preload next page on link hover (instant navigation)
    var preloadedUrls = {};
    document.addEventListener('pointerenter', function(e) {
      var link = e.target && e.target.closest ? e.target.closest('a[href^="/"]') : null;
      if (!link || preloadedUrls[link.href]) return;
      var url = link.href;
      // Don't preload external links or anchors
      if (url.indexOf('#') !== -1 || url.indexOf('mailto:') !== -1) return;
      preloadedUrls[url] = true;
      var hint = document.createElement('link');
      hint.rel = 'prefetch';
      hint.href = url;
      document.head.appendChild(hint);
    }, { passive: true, capture: true });

    // Intersection-based priority loading for product images
    if ('IntersectionObserver' in window) {
      var imgObserver = new IntersectionObserver(function(entries) {
        entries.forEach(function(entry) {
          if (entry.isIntersecting) {
            var img = entry.target;
            if (img.dataset.highres) {
              img.src = img.dataset.highres;
            }
            imgObserver.unobserve(img);
          }
        });
      }, { rootMargin: '200px' });

      $$('.pc-img--primary[data-highres]').forEach(function(img) {
        imgObserver.observe(img);
      });
    }
  }

  /* ============================================================
     SMART COUPON HINTS
     Shows contextual coupon suggestions based on cart value
  ============================================================ */
  function initSmartCouponHints() {
    document.addEventListener('cart:updated', function(e) {
      var cart = e.detail;
      if (!cart || !cart.total_price) return;
      var total = cart.total_price;
      var couponInput = $('#coupon-input');
      if (!couponInput) return;

      // Smart placeholder based on cart value
      if (total >= 3000 && total < 5000) {
        couponInput.placeholder = 'BEAUTY5';
      } else if (total >= 5000 && total < 8000) {
        couponInput.placeholder = 'BEAUTY10';
      } else if (total >= 8000) {
        couponInput.placeholder = 'VIP15';
      }
    });
  }

  /* ============================================================
     VIEWPORT OPTIMIZER
     Dynamically adjusts layout based on device capabilities
  ============================================================ */
  function initViewportOptimizer() {
    // Detect if user prefers reduced motion
    var prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) {
      document.documentElement.classList.add('reduced-motion');
    }

    // Detect slow connections and reduce image quality
    var conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (conn) {
      var slow = conn.effectiveType === '2g' || conn.effectiveType === 'slow-2g' || conn.saveData;
      if (slow) {
        document.documentElement.classList.add('save-data');
        // Reduce image sizes on slow connections
        $$('img[src*="width: 500"], img[src*="width=500"]').forEach(function(img) {
          img.src = img.src.replace(/width[=:]\s*500/g, 'width=300');
        });
      }
    }

    // Smart viewport height for mobile (fixes 100vh issue)
    function setVH() {
      var vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', vh + 'px');
    }
    setVH();
    window.addEventListener('resize', setVH, { passive: true });

    // Handle safe area insets for notched phones
    if (CSS.supports && CSS.supports('padding-bottom: env(safe-area-inset-bottom)')) {
      document.documentElement.classList.add('has-safe-area');
    }
  }

})();
